/** @jsxImportSource @emotion/react */
import { useAtomValue } from 'jotai'
import { useState, useEffect } from 'react'
import { Box, InputLabel, TextField, InputAdornment, useTheme } from '@mui/material'

import { Icon } from '@app/components/Atoms/Icon'
import { Button } from '@app/components/Atoms/Button'
import { Accordion } from '@app/components/Atoms/Accordion'
import { Autocomplete } from '@app/components/Atoms/Autocomplete/Autocomplete'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import {
  FILTERS,
  FILTER_LABEL_MAP,
  DEFAULT_FILTER_MAP,
  INITIAL_FILTER_STATE,
  COSTS_MAP,
} from '../utils/adaptationLibrary.constants'
import {
  AdaptationLibraryFilterItem,
  AdaptationLibraryFiltersType,
} from '../utils/adaptationLibrary.types'
import {
  filterAutocomplete,
  adaptionLibraryButton,
  adaptationLibrarySearchInput,
  adaptationLibraryFilterWrapper,
  adaptationLibraryFilterAccordion,
  adaptationLibraryFilterItemsWrapper,
  adaptationLibraryFilterHeaderWrapper,
} from '../styles/AdaptationLibrary.styles'

export const AdaptationLibraryFilters = ({
  filters,
  setFilters,
  uniqueFilterItems,
}: AdaptationLibraryFiltersType) => {
  const theme = useTheme()
  const [inputValue, setInputValue] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const currencySymbol = useAtomValue(currencySymbolAtom)

  function handleFilterChange(value: string[], filterName: AdaptationLibraryFilterItem) {
    // the x sets the value to an empty array, which resets it. Otherwise add te value to the filter
    const valueToSet = !value.length
      ? []
      : value.filter((item) => item !== DEFAULT_FILTER_MAP[filterName])

    setFilters({
      ...filters,
      [filterName]: valueToSet,
    })
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters({
        ...filters,
        search: inputValue.toLowerCase(),
      })
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [inputValue, setFilters, filters])

  function handleResetFilters() {
    setInputValue('')
    setFilters(INITIAL_FILTER_STATE)
  }

  return (
    <Box css={adaptationLibraryFilterWrapper()}>
      <Box sx={{ width: '100%', marginBottom: '16px' }}>
        <InputLabel
          htmlFor={'adaptationSearch'}
          style={{
            marginBottom: '16px',
            marginLeft: '2px',
            color: 'black',
            fontSize: '13px',
          }}
        >
          Search and filter intervention options below
        </InputLabel>
        <TextField
          id="adaptationSearch"
          value={inputValue}
          css={adaptationLibrarySearchInput()}
          placeholder="Search by intervention name, description"
          name="adaptationSearch"
          aria-describedby="adaptation library search"
          onChange={(e) => setInputValue(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="Search" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Accordion
        title="Filters"
        level="h3"
        onToggle={setFiltersOpen}
        header={
          <Box css={adaptationLibraryFilterHeaderWrapper()}>
            <p>Filter</p>
            <Box css={{ display: 'flex', gap: '8px' }}>
              <Button css={adaptionLibraryButton()}>
                {filtersOpen ? 'Hide filters' : 'Show filters'}
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleResetFilters()
                }}
                css={adaptionLibraryButton()}
              >
                {'Reset filters'}
              </Button>
            </Box>
          </Box>
        }
        toggleButton
        customStyle={adaptationLibraryFilterAccordion()}
        body={
          <Box css={adaptationLibraryFilterItemsWrapper()}>
            {FILTERS.map((filterItem: AdaptationLibraryFilterItem, index: number) => {
              let options: string[] = uniqueFilterItems[filterItem]
                ? [...uniqueFilterItems[filterItem]]
                : []
              if (filterItem === 'cost') {
                options = options.map((item) =>
                  COSTS_MAP[item as keyof typeof COSTS_MAP].replaceAll('$', currencySymbol || '$'),
                )
              }
              return (
                <Box key={filterItem + index}>
                  <InputLabel
                    htmlFor={filterItem}
                    style={{
                      marginBottom: '4px',
                      marginTop: '16px',
                      marginLeft: '2px',
                      color: 'black',
                      fontSize: '12px',
                    }}
                  >
                    {FILTER_LABEL_MAP[filterItem]}
                  </InputLabel>
                  <Autocomplete
                    value={
                      filters[filterItem].length
                        ? [...filters[filterItem]]
                        : [DEFAULT_FILTER_MAP[filterItem]]
                    }
                    options={options}
                    onChange={(event, value) => handleFilterChange(value, filterItem)}
                    customStyles={filterAutocomplete({ theme })}
                  />
                </Box>
              )
            })}
          </Box>
        }
      />
    </Box>
  )
}
